import { critical as _crit } from './ChatStreamLogger';
import { fetchWithIdTokenRetry } from './AuthHeaders';

// If the stream goes this long without an event (incl. heartbeat comments —
// those don't surface here because the consumer treats comments as noise)
// we warn that the connection may be silently dead.
const STALE_WARN_MS = 30000;

// Public chat SSE URLs embed `publicSessionToken` as a path segment after
// `/session/`. The token is a bearer secret, so redact it before logging
// while keeping enough path info to debug reconnects.
function redactUrlForLog(url) {
  if (typeof url !== 'string' || !url) return url;
  return url.replace(/\/session\/[^/?#]+/g, '/session/***');
}

function getRetryDelayMs(retryNumber) {
  return Math.min(1000 * Math.pow(2, retryNumber - 1), 10000);
}

function sleep(ms, signal) {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const finish = () => {
      clearTimeout(timer);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const timer = setTimeout(finish, ms);
    signal.addEventListener('abort', finish, { once: true });
  });
}

function emitDiagnostic(onStreamDiagnostic, info) {
  if (typeof onStreamDiagnostic === 'function') {
    try {
      onStreamDiagnostic({ ...info, timestamp: Date.now() });
    } catch (_) {}
  }
}

async function consumeJsonSse(response, onEvent, onStreamDiagnostic, logLabel) {
  const reader = response.body?.getReader();
  if (!reader) {
    _crit("SSE", "error", `[${logLabel}] no reader on response body`);
    throw new Error("SSE response has no readable body");
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let eventName = null;
  let eventId = null;
  let dataLines = [];
  let dispatchCount = 0;
  let byteCount = 0;
  let lastByteAt = Date.now();
  let staleWarned = false;
  let staleError = null;

  // Watchdog: if we're connected but the TCP stream goes silent (no data, no
  // heartbeat comments) past STALE_WARN_MS, something is wrong — the backend
  // heartbeat scheduler is broken, the LB killed the connection, or similar.
  const staleWatchdog = setInterval(() => {
    const silentMs = Date.now() - lastByteAt;
    if (silentMs > STALE_WARN_MS && !staleWarned) {
      staleWarned = true;
      _crit(
        "SSE",
        "warn",
        `[${logLabel}] STALE — no bytes from server for ${silentMs}ms (heartbeats should arrive every 15s). Connection may be silently dead.`
      );
      emitDiagnostic(onStreamDiagnostic, { event: 'stale', silentMs });
      staleError = new Error("SSE connection stopped receiving heartbeats");
      // Unblock read() so retry/polling can recover a half-open connection.
      void reader.cancel().catch(() => {});
    }
  }, 5000);

  const dispatchEvent = () => {
    const name = eventName;
    const id = eventId;
    eventName = null;
    eventId = null;
    if (dataLines.length === 0) return;
    const rawData = dataLines.join('\n');
    dataLines = [];
    let parsed;
    try {
      parsed = JSON.parse(rawData);
    } catch {
      _crit("SSE", "error", `[${logLabel}] PARSE ERROR event=${name}`);
      emitDiagnostic(onStreamDiagnostic, { event: 'parse_error' });
      return;
    }
    // Handler failures must reach the retry path, not masquerade as bad JSON.
    onEvent(name, parsed, id);
    dispatchCount++;
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (staleError) throw staleError;
      if (done) {
        buffer += decoder.decode();
        buffer += '\n\n';
      } else {
        lastByteAt = Date.now();
        staleWarned = false;
        byteCount += value?.byteLength || 0;
        buffer += decoder.decode(value, { stream: true });
      }
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const normalizedLine = line.endsWith('\r') ? line.slice(0, -1) : line;
        if (normalizedLine.startsWith('event:')) {
          eventName = normalizedLine.slice(6).trim();
        } else if (normalizedLine.startsWith('data:')) {
          dataLines.push(normalizedLine.slice(5));
        } else if (normalizedLine.startsWith('id:')) {
          eventId = normalizedLine.slice(3).trim();
        } else if (normalizedLine === '') {
          dispatchEvent();
        }
      }

      if (done) {
        return { dispatchCount, byteCount, closedByServer: true };
      }
    }
  } finally {
    clearInterval(staleWatchdog);
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

function emitConnectionState(onConnectionStateChange, state) {
  if (typeof onConnectionStateChange === 'function') {
    onConnectionStateChange(state);
  }
}

export async function streamJsonSseWithRetry({
  url,
  abortController,
  onEvent,
  shouldReconnect,
  isComplete,
  onConnectionStateChange,
  onStreamDiagnostic,
  getReconnectUrl,
  maxRetries = 5,
  logLabel = 'SSE stream',
  skipAuth = false,
}) {
  let retries = 0;
  let lastEventId = null;
  let activeUrl = url;

  while (retries <= maxRetries) {
    if (abortController.signal.aborted) {
      emitConnectionState(onConnectionStateChange, 'idle');
      return { aborted: true, connectionFailed: false };
    }
    if (isComplete?.()) {
      emitConnectionState(onConnectionStateChange, 'idle');
      return { aborted: false, connectionFailed: false };
    }
    const connectStartedAt = Date.now();
    try {
      const connState = retries === 0 ? 'connecting' : 'reconnecting';
      emitConnectionState(onConnectionStateChange, connState);
      const loggedUrl = redactUrlForLog(activeUrl);
      if (retries === 0) {
        _crit("SSE", "info", `[${logLabel}] connecting → ${loggedUrl}`);
      } else {
        _crit("SSE", "warn", `[${logLabel}] RECONNECTING (retry ${retries}/${maxRetries}) → ${loggedUrl}`);
      }
      const fetchOptions = {
        headers: { Accept: 'text/event-stream' },
        signal: abortController.signal,
      };
      const response = skipAuth
        ? await fetch(activeUrl, fetchOptions)
        : await fetchWithIdTokenRetry(activeUrl, {
            ...fetchOptions,
            includeUserId: true,
          });
      if (response.status === 204) {
        _crit("SSE", "info", `[${logLabel}] 204 No Content — session already terminal, closing stream`);
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: false, connectionFailed: false, noContent: true };
      }

      if (!response.ok) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      emitConnectionState(onConnectionStateChange, 'connected');
      emitDiagnostic(onStreamDiagnostic, { event: 'connect' });
      _crit("SSE", "info", `[${logLabel}] connected (status=${response.status})`);

      const consumeResult = await consumeJsonSse(
        response,
        (eventName, parsed, eventId) => {
          onEvent(eventName, parsed, eventId);
          // Reset backoff only after useful progress, not merely HTTP 200.
          retries = 0;
          if (eventId) {
            const numericId = Number(eventId);
            if (Number.isFinite(numericId)) lastEventId = Math.max(lastEventId ?? 0, numericId);
          }
        },
        onStreamDiagnostic,
        logLabel
      );
      const willReconnect = !isComplete?.() && shouldReconnect && shouldReconnect();
      const openMs = Date.now() - connectStartedAt;
      _crit(
        "SSE",
        willReconnect ? "warn" : "info",
        `[${logLabel}] stream closed by server after ${openMs}ms — events=${consumeResult.dispatchCount} bytes=${consumeResult.byteCount} lastEventId=${lastEventId} willReconnect=${!!willReconnect}`
      );

      if (willReconnect) {
        retries++;
        if (typeof getReconnectUrl === 'function' && lastEventId) {
          const reconnectUrl = getReconnectUrl(lastEventId);
          if (reconnectUrl) activeUrl = reconnectUrl;
        }
        emitConnectionState(onConnectionStateChange, 'reconnecting');
        emitDiagnostic(onStreamDiagnostic, { event: 'reconnect', retry: retries });
        if (retries > maxRetries) break;
        await sleep(getRetryDelayMs(retries), abortController.signal);
        continue;
      }

      emitConnectionState(onConnectionStateChange, 'idle');
      return { aborted: false, connectionFailed: false };
    } catch (e) {
      if (e?.name === 'AbortError' || abortController.signal.aborted) {
        _crit("SSE", "info", `[${logLabel}] aborted by client`);
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: true, connectionFailed: false };
      }

      // A terminal event or REST reconciliation may have settled the turn before
      // the socket reports its closure as an error. Completion is not a disconnect.
      if (isComplete?.()) {
        emitConnectionState(onConnectionStateChange, 'idle');
        return { aborted: false, connectionFailed: false };
      }
      retries++;
      const openMs = Date.now() - connectStartedAt;
      if (retries > maxRetries) {
        emitConnectionState(onConnectionStateChange, 'disconnected');
        emitDiagnostic(onStreamDiagnostic, { event: 'disconnect', reason: 'max_retries' });
        _crit("SSE", "error", `[${logLabel}] GAVE UP after ${maxRetries} retries (openMs=${openMs}): ${e?.message || e}`);
        return { aborted: false, connectionFailed: true };
      }

      if (typeof getReconnectUrl === 'function' && lastEventId) {
        const reconnectUrl = getReconnectUrl(lastEventId);
        if (reconnectUrl) activeUrl = reconnectUrl;
      }

      emitConnectionState(onConnectionStateChange, 'reconnecting');
      const delay = getRetryDelayMs(retries);
      _crit("SSE", "warn", `[${logLabel}] errored after ${openMs}ms, retrying in ${delay}ms (${retries}/${maxRetries}): ${e?.message || e}`);
      await sleep(delay, abortController.signal);
    }
  }

  emitConnectionState(onConnectionStateChange, 'disconnected');
  return { aborted: false, connectionFailed: true };
}
